import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { GraduationCap, Users, ArrowLeft, Loader2, Mail, Lock, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADES = [
  { value: '6th', label: '6th Grade' },
  { value: '7th', label: '7th Grade' },
  { value: '8th', label: '8th Grade' },
  { value: '9th', label: '9th Grade (Freshman)' },
  { value: '10th', label: '10th Grade (Sophomore)' },
  { value: '11th', label: '11th Grade (Junior)' },
  { value: '12th', label: '12th Grade (Senior)' }
];

const StudentParentAuth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('student');

  const [studentForm, setStudentForm] = useState({
    name: '',
    email: '',
    password: '',
    age: '',
    grade: '',
    parent_email: ''
  });

  const [parentForm, setParentForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleStudentRegister = async (e) => {
    e.preventDefault();
    
    const age = parseInt(studentForm.age);
    if (age < 11 || age > 18) {
      toast.error('Age must be between 11 and 18');
      return;
    }

    if (studentForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/student/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: studentForm.name,
          email: studentForm.email,
          password: studentForm.password,
          age: age,
          grade: studentForm.grade,
          parent_email: studentForm.parent_email || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const data = await response.json();
      // Token now stored in httpOnly cookie by backend
      toast.success('Welcome! Your account has been created.');
      navigate('/student/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleParentRegister = async (e) => {
    e.preventDefault();

    if (parentForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/parent/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: parentForm.name,
          email: parentForm.email,
          password: parentForm.password
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const data = await response.json();
      // Token now stored in httpOnly cookie by backend
      toast.success('Welcome! Your account has been created.');
      navigate('/parent/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(260,25%,97%)] via-white to-[hsl(200,20%,96%)] flex flex-col">
      <div className="p-6">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
          style={{transition: 'color 0.2s ease'}}
          data-testid="back-to-home"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">Back to Home</span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[hsl(260,30%,52%)] to-[hsl(260,30%,46%)] flex items-center justify-center">
                <GraduationCap className="h-7 w-7 text-white" strokeWidth={1.5} />
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-600 to-slate-900 flex items-center justify-center">
                <Users className="h-7 w-7 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-foreground">Student & Parent Portal</h1>
            <p className="text-muted-foreground mt-1">Learn and grow together with emotional intelligence</p>
          </div>

          <Card className="rounded-2xl border-[hsl(260,20%,90%)]  bg-black/80">
            <CardContent className="pt-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 rounded-xl bg-muted/50 p-1 mb-6">
                  <TabsTrigger value="student" className="rounded-lg" data-testid="student-tab">
                    <GraduationCap className="h-4 w-4 mr-2" />
                    Student
                  </TabsTrigger>
                  <TabsTrigger value="parent" className="rounded-lg" data-testid="parent-tab">
                    <Users className="h-4 w-4 mr-2" />
                    Parent
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="student">
                  <form onSubmit={handleStudentRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Your name"
                          className="pl-10 rounded-xl h-12"
                          value={studentForm.name}
                          onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                          required
                          data-testid="student-name-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Age</Label>
                        <Input
                          type="number"
                          placeholder="11-18"
                          min="11"
                          max="18"
                          className="rounded-xl h-12"
                          value={studentForm.age}
                          onChange={(e) => setStudentForm({ ...studentForm, age: e.target.value })}
                          required
                          data-testid="student-age-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Grade</Label>
                        <Select 
                          value={studentForm.grade} 
                          onValueChange={(value) => setStudentForm({ ...studentForm, grade: value })}
                        >
                          <SelectTrigger className="rounded-xl h-12" data-testid="student-grade-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {GRADES.map((grade) => (
                              <SelectItem key={grade.value} value={grade.value}>
                                {grade.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          className="pl-10 rounded-xl h-12"
                          value={studentForm.email}
                          onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                          required
                          data-testid="student-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="password"
                          placeholder="Min 6 characters"
                          className="pl-10 rounded-xl h-12"
                          value={studentForm.password}
                          onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
                          required
                          data-testid="student-password-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Parent's Email (Optional)</Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="parent@email.com"
                          className="pl-10 rounded-xl h-12"
                          value={studentForm.parent_email}
                          onChange={(e) => setStudentForm({ ...studentForm, parent_email: e.target.value })}
                          data-testid="parent-email-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Adding your parent's email lets them track your progress
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full rounded-full h-12 font-medium mt-2"
                      disabled={loading}
                      data-testid="student-register-btn"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create Student Account
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="parent">
                  <form onSubmit={handleParentRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Your name"
                          className="pl-10 rounded-xl h-12"
                          value={parentForm.name}
                          onChange={(e) => setParentForm({ ...parentForm, name: e.target.value })}
                          required
                          data-testid="parent-name-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          className="pl-10 rounded-xl h-12"
                          value={parentForm.email}
                          onChange={(e) => setParentForm({ ...parentForm, email: e.target.value })}
                          required
                          data-testid="parent-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="password"
                          placeholder="Min 6 characters"
                          className="pl-10 rounded-xl h-12"
                          value={parentForm.password}
                          onChange={(e) => setParentForm({ ...parentForm, password: e.target.value })}
                          required
                          data-testid="parent-password-input"
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full rounded-full h-12 font-medium mt-2"
                      disabled={loading}
                      data-testid="parent-register-btn"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create Parent Account
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              <p className="text-center text-sm text-muted-foreground mt-6">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StudentParentAuth;
